import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';

const steps = [
  {
    step: '01',
    icon: 'ri-smartphone-line',
    time: '0h',
    title: 'LINEで写真を送るだけ。',
    description:
      'トイレの現状写真をLINEで送るだけ。面倒な現地調査や電話での長い説明は不要。スキマ時間に完結します。',
    points: ['写真1枚でOK', '返信は最短30分', '完全無料'],
    color: 'bg-white',
  },
  {
    step: '02',
    icon: 'ri-file-list-3-line',
    time: '数時間以内',
    title: 'その場で正確な見積もり。',
    description:
      '写真をもとに正確な見積もりをご提示。追加費用一切なしのコミコミ価格で、安心してご検討いただけます。',
    points: ['コミコミ価格で明朗会計', '複数プランを比較可能', '後から費用追加なし'],
    color: 'bg-navy/5',
  },
  {
    step: '03',
    icon: 'ri-hammer-line',
    time: '48時間以内',
    title: '48時間以内に着工。',
    description:
      'ご了承いただいたら最短48時間以内に職人がお伺いし、着工開始。多能工1名が全工程を担当するため、最短1日で完工します。',
    points: ['多能工1名が全工程担当', '最短1日で完工', '完工後のアフターフォローあり'],
    color: 'bg-white',
  },
];

export default function ProcessSection() {
  const { ref, isVisible } = useIntersectionObserver(0.1);

  return (
    <section id="process" className="py-20 md:py-28 bg-white overflow-hidden">
      <div
        ref={ref}
        className="max-w-7xl mx-auto px-4 md:px-8"
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.7s ease, transform 0.7s ease',
        }}
      >
        <div className="text-center mb-14">
          <span className="inline-block text-accent font-display font-bold text-xs tracking-widest uppercase mb-3">
            Process
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-navy leading-tight mb-3">
            タイパ重視の<span className="text-accent">3ステップ</span>
          </h2>
          <div className="w-16 h-1 bg-accent mx-auto rounded-full mb-4"></div>
          <p className="text-gray-500 text-sm md:text-base max-w-lg mx-auto leading-relaxed">
            忙しい毎日に合わせた、かんたん・はやい・安心の3ステップ。
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4 relative">
          <div className="hidden md:block absolute top-12 left-[calc(33%+1rem)] right-[calc(33%+1rem)] h-0.5 bg-accent/30 z-0">
            <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-accent text-lg"></i>
            </div>
            <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-accent text-lg"></i>
            </div>
          </div>

          {steps.map((step, index) => (
            <div
              key={step.step}
              className="relative bg-white rounded-2xl border border-gray-100 p-7 md:p-8 z-10"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.7s ease ${index * 0.15}s, transform 0.7s ease ${index * 0.15}s`,
              }}
            >
              <div className="flex items-center justify-between mb-5">
                <span className="font-display font-black text-accent/20 text-6xl leading-none select-none">
                  {step.step}
                </span>
                <span className="flex items-center gap-1 text-xs text-gray-400 bg-gray-50 px-3 py-1 rounded-full">
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-time-line text-xs"></i>
                  </span>
                  {step.time}
                </span>
              </div>

              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-navy/5 mb-4">
                <i className={`${step.icon} text-navy text-xl`}></i>
              </div>

              <h3 className="font-black text-navy text-xl md:text-2xl mb-3 leading-tight">
                STEP {index + 1}<br />
                {step.title}
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-5">{step.description}</p>

              <ul className="space-y-2">
                {step.points.map((point) => (
                  <li key={point} className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-accent text-sm"></i>
                    </span>
                    {point}
                  </li>
                ))}
              </ul>

              {index === 2 && (
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-400">完工後もアフターサポートで安心</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div
          className="mt-10 rounded-2xl overflow-hidden"
          style={{
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 0.7s ease 0.5s',
          }}
        >
          <div className="bg-navy p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="font-display font-black text-5xl md:text-6xl text-accent leading-none flex-shrink-0">
                48h
              </div>
              <div>
                <p className="text-white font-bold text-lg md:text-xl leading-tight mb-1">
                  業界最速の着工保証
                </p>
                <p className="text-white/70 text-sm">
                  LINE相談 → 見積もり → 着工まで、最短48時間以内。<br />
                  あなたのスケジュールに合わせて動きます。
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                const el = document.querySelector('#contact');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="flex items-center gap-2 bg-accent hover:bg-accent-dark text-white px-7 py-4 rounded-full font-bold text-sm whitespace-nowrap transition-all duration-300 shadow-lg cursor-pointer flex-shrink-0"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-line-fill text-base"></i>
              </span>
              今すぐLINEで相談する
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
